/**
 * "Search case" field in the case top bar (FR-04, sc-11033). Typing (debounced)
 * or Enter submits the query, which opens the results drawer beside the viewer;
 * Escape clears it.
 */
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

const DEBOUNCE_MS = 350;

interface CaseSearchFieldProps {
  /** Called with the trimmed query; '' when cleared. */
  onSearch: (query: string) => void;
}

export function CaseSearchField({ onSearch }: CaseSearchFieldProps) {
  const [value, setValue] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const submit = (next: string, delay: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearchRef.current(next.trim()), delay);
  };

  return (
    <label className="relative flex items-center" htmlFor="case-search-input">
      <Search className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-gray-500" aria-hidden />
      <input
        id="case-search-input"
        type="search"
        value={value}
        placeholder="Search case"
        aria-label="Search case"
        autoComplete="off"
        maxLength={200}
        onChange={(e) => {
          setValue(e.target.value);
          if (e.target.value.trim()) submit(e.target.value, DEBOUNCE_MS);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            submit(value, 0);
          } else if (e.key === 'Escape') {
            setValue('');
            submit('', 0);
          }
        }}
        className="h-8 w-56 rounded-md border border-border bg-white pl-7 pr-7 text-sm outline-none placeholder:text-gray-500 focus:border-primary focus:ring-2 focus:ring-primary/30 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          className="absolute right-1.5 inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
          aria-label="Clear search"
          onClick={() => {
            setValue('');
            submit('', 0);
          }}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </label>
  );
}
